import React, { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from "../../auth/providers/AuthProvider";
import { ThemeContext } from 'styled-components';
import { CsrfContext } from "../../../providers/CsrfProvider";
import { Button, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {getLinkToken, linkAccount, listLinkedAccounts, updateLinkedAccount, deleteLinkedAccount} from "../../../services/Banking";
import {useMutation, useQuery, useQueryClient} from "react-query";
import InputValidatorField from "../../common/components/Inputs/InputValidatorField";
import ButtonPrimary from "../../common/components/Buttons/ButtonPrimary";
import {Box} from "@mui/system";

const LinkedAccountsModel = () => {
    const theme = useContext(ThemeContext);
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [linkToken, setLinkToken] = useState(null);
    const queryClient = useQueryClient();
    const [editingAccountId, setEditingAccountId] = useState(null);
    const [editingVenmoId, setEditingVenmoId] = useState('');
    const [editingAccount, setEditingAccount] = useState(null);
    const [isAddingAccount, setIsAddingAccount] = useState(false);
    const [newVenmoId, setNewVenmoId] = useState('');
    const [confirmVenmoId, setConfirmVenmoId] = useState('');


    // Plaid Link setup
    const plaidLink = useRef(null);


    useEffect(() => {
        if (authClaims && authClaims.groups && authClaims.groups.includes("BetaAccess")) {
            // Load the Plaid script
            const script = document.createElement('script');
            script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
            script.async = true;
            script.onload = () => setScriptLoaded(true);
            script.onerror = () => {};
            document.body.appendChild(script);

            // Fetch the link token
            const fetchToken = async () => {
                try {
                    const token = await getLinkToken(csrfToken);
                    setLinkToken(token);
                } catch (error) {
                    // Error fetching link token
                }
            };

            fetchToken();

            return () => {
                document.body.removeChild(script);
            };
        }
    }, [csrfToken]);  // Re-run the effect if authToken or csrfToken changes

    useEffect(() => {
        if (scriptLoaded && linkToken) {
            initializePlaidLink(linkToken);
        }
    }, [scriptLoaded, linkToken]);  // Only run this effect when scriptLoaded or linkToken changes

    const { data: accounts, isLoading: isQueryLoading, isError: isQueryError } = useQuery(
        ['accounts'],
        listLinkedAccounts(csrfToken)
    );

    const initializePlaidLink = (linkToken) => {
        if (!linkToken) return;

        const plaid = window.Plaid;
        if (plaid) {
            plaidLink.current = plaid.create({
                token: linkToken,
                onSuccess: async (public_token, metadata) => {
                    try {
                        await linkAccount(csrfToken, public_token, "Plaid");
                        await queryClient.invalidateQueries(['accounts']);
                    } catch (error) {
                        // console.error('Error exchanging public token:', error);
                    }
                },
                onExit: (err, metadata) => {
                    // console.error('Plaid onExit:', err, metadata);
                }
            });
        }
    };

    const handleAddPlaidAccount = async () => {
        try {
            if (!!linkToken) {
                initializePlaidLink(linkToken)

                // Assuming plaidLink.current holds the Plaid instance and should be opened only after receiving the token.
                if (plaidLink.current) {
                    plaidLink.current.open();
                }
            }
        } catch (error) {
            // Handle errors (e.g., display an error message)
        }
    };

    const handleStartEdit = (account) => {
        setIsAddingAccount(false); // Collapse add form if open
        // If clicking the same account, this will just refresh the edit state
        // If clicking a different account, it will switch to editing that one
        setEditingAccountId(account.id);
        setEditingVenmoId(account.mask); // mask contains the Venmo ID
        setEditingAccount(account);
    };

    const handleCancelEdit = () => {
        setEditingAccountId(null);
        setEditingVenmoId('');
        setEditingAccount(null);
    };

    const handleUpdateAccount = async (e) => {
        e.preventDefault();
        if (!editingVenmoId.trim()) return;

        try {
            await updateLinkedAccount(csrfToken, editingAccountId, editingVenmoId.trim());
            setEditingAccountId(null);
            setEditingVenmoId('');
            setEditingAccount(null);
            await queryClient.invalidateQueries(['accounts']);
        } catch (error) {
            alert('Error updating account: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteAccount = async () => {
        if (!editingAccount) return;
        
        if (!window.confirm(`Are you sure you want to remove this Venmo account "${editingAccount.mask}"?`)) {
            return;
        }

        try {
            await deleteLinkedAccount(csrfToken, editingAccount.id);
            await queryClient.invalidateQueries(['accounts']);
            setEditingAccountId(null);
            setEditingVenmoId('');
            setEditingAccount(null);
        } catch (error) {
            alert('Error deleting account: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleStartAdd = () => {
        setEditingAccountId(null); // Collapse any edit form if open
        setEditingVenmoId('');
        setEditingAccount(null);
        setIsAddingAccount(true);
        setNewVenmoId('');
        setConfirmVenmoId('');
    };

    const handleCancelAdd = () => {
        setIsAddingAccount(false);
        setNewVenmoId('');
        setConfirmVenmoId('');
    };

    const handleAddAccount = async (e) => {
        e.preventDefault();
        if (!newVenmoId.trim() || newVenmoId !== confirmVenmoId) return;

        try {
            await linkAccount(csrfToken, newVenmoId.trim(), "Venmo");
            await queryClient.invalidateQueries(['accounts']);
            setIsAddingAccount(false);
            setNewVenmoId('');
            setConfirmVenmoId('');
        } catch (error) {
            alert('Error linking account: ' + (error.response?.data?.error || error.message));
        }
    };

    const isValidVenmoId = (value) => {
        if (!value) return true;
        return /^@[a-zA-Z0-9_-]{5,16}$/.test(value);
    };

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 'none',
                    borderBottom: 'none',
                    mb: '40px',
                    '& .MuiTableContainer-root': {
                        borderBottom: 'none !important',
                        '&::after': {
                            display: 'none !important',
                        },
                    },
                }}
            >
                {isQueryLoading ? (
                    <Typography sx={{color:theme.colors.text, textAlign: 'center', py: 2}}>Loading...</Typography>
                ) : isQueryError ? (
                    <Typography sx={{color:theme.colors.text, textAlign: 'center', py: 2}}>Error loading accounts</Typography>
                ) : (
                    <TableContainer
                        component={Box}
                        sx={{
                            mb: 0,
                            pb: 0,
                            mt: 0,
                            pt: 0,
                            mr: 0,
                            ml: 0,
                            width: '100%',
                            borderRadius: 2,
                            backgroundColor: theme.colors.tableBackground || theme.colors.background,
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                            outline: 'none !important',
                            overflow: 'hidden',
                            '&::before': {
                                display: 'none !important',
                            },
                            '&::after': {
                                display: 'none !important',
                            },
                            '&.MuiPaper-root': {
                                border: 'none !important',
                                borderBottom: 'none !important',
                                borderTop: 'none !important',
                                boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                                marginBottom: '0 !important',
                                paddingBottom: '0 !important',
                                outline: 'none !important',
                                borderRadius: 2,
                            },
                            '& .MuiPaper-root': {
                                border: 'none !important',
                                borderBottom: 'none !important',
                                borderTop: 'none !important',
                                boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                                marginBottom: '0 !important',
                                paddingBottom: '0 !important',
                                outline: 'none !important',
                                borderRadius: 2,
                            },
                            '& .MuiTable-root': {
                                border: 'none !important',
                                borderBottom: 'none !important',
                                borderTop: 'none !important',
                                marginBottom: '0 !important',
                                '& .MuiTableBody-root': {
                                    border: 'none !important',
                                    borderBottom: 'none !important',
                                    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                                        borderBottom: 'none !important',
                                    },
                                },
                            },
                        }}
                    >
                        <Table sx={{
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderCollapse: 'collapse',
                            borderSpacing: 0,
                            width: '100%',
                            tableLayout: 'auto',
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    <TableCell sx={{
                                        color: theme.colors.brandSecondary,
                                        fontWeight: 700,
                                        pl: 0,
                                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                        py: 2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 0,
                                    }}>Financial Accounts</TableCell>
                                    <TableCell sx={{
                                        width: '3.5rem !important',
                                        minWidth: '3.5rem !important',
                                        maxWidth: '3.5rem !important',
                                        p: 0,
                                        flexShrink: 0,
                                        flexGrow: 0,
                                    }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Account Rows */}
                                {accounts && accounts.length > 0 ? (
                                    accounts.map((account, index) => (
                                        <React.Fragment key={account.id || index}>
                                            {/* Header Row (when editing) */}
                                            {editingAccountId === account.id && (
                                                <TableRow>
                                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                                                    <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                                                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                                            Edit Account
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ 
                                                        width: '3.5rem !important', 
                                                        minWidth: '3.5rem !important', 
                                                        maxWidth: '3.5rem !important', 
                                                        p: 0,
                                                        flexShrink: 0,
                                                        flexGrow: 0,
                                                        borderBottom: 'none',
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                            height: '100%'
                                                        }}>
                                                            <IconButton
                                                                onClick={handleCancelEdit}
                                                                size="small"
                                                                sx={{
                                                                    color: 'inherit',
                                                                    padding: '4px',
                                                                    '&:hover': {
                                                                        backgroundColor: theme.colors.overlayHover,
                                                                    },
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            
                                            {/* Form Row (when editing) */}
                                            {editingAccountId === account.id && (
                                                <TableRow>
                                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                                    <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 2 }}>
                                                        <Box
                                                            component="form"
                                                            onSubmit={handleUpdateAccount}
                                                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                                        >
                                                            <InputValidatorField
                                                                label="Venmo ID"
                                                                placeholder="@username"
                                                                value={editingVenmoId}
                                                                handleChange={(e) => setEditingVenmoId(e.target.value)}
                                                                name="venmoId"
                                                                isValidInput={(value) => {
                                                                    if (!value) return true;
                                                                    return /^@[a-zA-Z0-9_-]{5,16}$/.test(value);
                                                                }}
                                                                helperText="Enter your Venmo username (starts with @)"
                                                            />
                                                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                                                <Button
                                                                    type="button"
                                                                    onClick={handleDeleteAccount}
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    sx={{
                                                                        border: `1px solid ${theme.colors.error || '#f44336'}`,
                                                                        backgroundColor: 'transparent',
                                                                        color: theme.colors.error || '#f44336',
                                                                        '&:hover': {
                                                                            borderColor: theme.colors.error || '#f44336',
                                                                            backgroundColor: theme.colors.errorHover || 'rgba(244, 67, 54, 0.1)',
                                                                        },
                                                                    }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                                {(!editingVenmoId.trim() || 
                                                                  editingVenmoId === editingAccount?.mask ||
                                                                  !/^@[a-zA-Z0-9_-]{5,16}$/.test(editingVenmoId)) ? (
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleCancelEdit}
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        sx={{
                                                                            border: `1px solid ${theme.colors.border || '#ccc'}`,
                                                                            backgroundColor: 'transparent',
                                                                            color: theme.colors.text,
                                                                            '&:hover': {
                                                                                borderColor: theme.colors.border || '#ccc',
                                                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        type="submit"
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        sx={{
                                                                            border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                                                            backgroundColor: 'transparent',
                                                                            color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                            '&:hover': {
                                                                                borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                                </TableRow>
                                            )}
                                            
                                            {/* Normal Row (when not editing) */}
                                            {editingAccountId !== account.id && (
                                            <TableRow>
                                                <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                                <TableCell sx={{
                                                    color: theme.colors.text,
                                                    pl: 0,
                                                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 0,
                                                    fontWeight: 'bold',
                                                }}>
                                                    {account.name === 'Venmo' ? (
                                                        <>
                                                            Venmo • {account.mask}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {account.name}
                                                            {account.subtype && ` • ${account.subtype}`}
                                                            {account.mask && ` • ****${account.mask}`}
                                                        </>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{
                                                    width: '3.5rem !important',
                                                    minWidth: '3.5rem !important',
                                                    maxWidth: '3.5rem !important',
                                                    p: 0,
                                                    flexShrink: 0,
                                                    flexGrow: 0,
                                                }}>
                                                    {account.name === 'Venmo' && (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                        }}>
                                                            <IconButton
                                                                onClick={() => handleStartEdit(account)}
                                                                size="small"
                                                                sx={{
                                                                    color: theme.colors.text,
                                                                    padding: '4px',
                                                                    '&:hover': {
                                                                        backgroundColor: theme.colors.overlayHover,
                                                                    },
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))
                                ) : !isAddingAccount && (
                                    <TableRow>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                        <TableCell sx={{
                                            color: theme.colors.text,
                                            pl: 0,
                                            py: 2,
                                            fontStyle: 'italic',
                                            opacity: 0.7,
                                        }}>
                                            No linked accounts
                                        </TableCell>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    </TableRow>
                                )}

                                {/* Header Row (when adding) */}
                                {isAddingAccount && (
                                    <TableRow>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                                        <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                                            <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                                Link Account
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            width: '3.5rem !important', 
                                            minWidth: '3.5rem !important', 
                                            maxWidth: '3.5rem !important', 
                                            p: 0,
                                            flexShrink: 0,
                                            flexGrow: 0,
                                            borderBottom: 'none',
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                                height: '100%'
                                            }}>
                                                <IconButton
                                                    onClick={handleCancelAdd}
                                                    size="small"
                                                    sx={{
                                                        color: 'inherit',
                                                        padding: '4px',
                                                        '&:hover': {
                                                            backgroundColor: theme.colors.overlayHover,
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Form Row (when adding) */}
                                {isAddingAccount && (
                                    <TableRow>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                        <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 2 }}>
                                            {accounts && accounts.some(acc => acc.name === 'Venmo') ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Typography sx={{ 
                                                        color: theme.colors.text, 
                                                        fontStyle: 'italic',
                                                        opacity: 0.8 
                                                    }}>
                                                        Only Venmo supported at this time
                                                    </Typography>
                                                    <Button
                                                        type="button"
                                                        onClick={handleCancelAdd}
                                                        variant="outlined"
                                                        fullWidth
                                                        sx={{
                                                            border: `1px solid ${theme.colors.border || '#ccc'}`,
                                                            backgroundColor: 'transparent',
                                                            color: theme.colors.text,
                                                            '&:hover': {
                                                                borderColor: theme.colors.border || '#ccc',
                                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                            },
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Box
                                                    component="form"
                                                    onSubmit={handleAddAccount}
                                                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                                >
                                                    <InputValidatorField
                                                        label="Venmo ID"
                                                        placeholder="@username"
                                                        value={newVenmoId}
                                                        handleChange={(e) => setNewVenmoId(e.target.value)}
                                                        name="venmoId"
                                                        isValidInput={isValidVenmoId}
                                                        helperText="Enter your Venmo username (starts with @)"
                                                    />
                                                    {newVenmoId && (
                                                        <InputValidatorField
                                                            label="Confirm Venmo ID"
                                                            placeholder="@username"
                                                            value={confirmVenmoId}
                                                            handleChange={(e) => setConfirmVenmoId(e.target.value)}
                                                            name="confirmVenmoId"
                                                            isValidInput={(value) => !value || value === newVenmoId}
                                                            helperText="Confirm your Venmo username"
                                                            error={confirmVenmoId && confirmVenmoId !== newVenmoId ? "Venmo IDs must match" : ""}
                                                        />
                                                    )}
                                                    {(!newVenmoId.trim() || newVenmoId !== confirmVenmoId || !isValidVenmoId(newVenmoId)) ? (
                                                        <Button
                                                            type="button"
                                                            onClick={handleCancelAdd}
                                                            variant="outlined"
                                                            fullWidth
                                                            sx={{
                                                                border: `1px solid ${theme.colors.border || '#ccc'}`,
                                                                backgroundColor: 'transparent',
                                                                color: theme.colors.text,
                                                                '&:hover': {
                                                                    borderColor: theme.colors.border || '#ccc',
                                                                    backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                },
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type="submit"
                                                            variant="outlined"
                                                            fullWidth
                                                            sx={{
                                                                border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                                                backgroundColor: 'transparent',
                                                                color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                '&:hover': {
                                                                    borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                    backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                },
                                                            }}
                                                        >
                                                            Link
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    </TableRow>
                                )}

                                {/* Link Venmo Button Row */}
                                {!editingAccountId && !isAddingAccount && (
                                    <TableRow>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                        <TableCell sx={{ pl: 0, pr: 0, py: 1, maxWidth: 0 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={handleStartAdd}
                                                startIcon={<AddIcon />}
                                                fullWidth
                                                sx={{
                                                    borderColor: theme.current === 'light'
                                                        ? theme.colors.brandSecondary
                                                        : (theme.colors.secondary || theme.colors.brandSecondary),
                                                    color: theme.current === 'light' 
                                                        ? '#000000' 
                                                        : (theme.colors.secondary || theme.colors.brandSecondary),
                                                    backgroundColor: theme.current === 'light'
                                                        ? theme.colors.brandSecondary
                                                        : (theme.colors.tableBackground || theme.colors.background),
                                                    '&:hover': {
                                                        borderColor: theme.current === 'light'
                                                            ? theme.colors.brandSecondary
                                                            : (theme.colors.secondary || theme.colors.brandSecondary),
                                                        backgroundColor: theme.current === 'light'
                                                            ? theme.colors.brandSecondary
                                                            : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                                    },
                                                }}
                                            >
                                                Link Account
                                            </Button>
                                        </TableCell>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </>
    );
};

export default LinkedAccountsModel;
